/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { get } from 'lodash/object';
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { DriftService } from './services/drift.service';
import { SentryService } from './services/sentry.service';

import { AuthService } from './services/auth.service';
import { EventService } from './services/event.service';
import { SessionService } from './services/session2.service';
import { VersionService } from './services/version.service';
import { ServiceWorkerService } from './services/sw.service';
import { Constants } from './global/constants';
import { isSunsetBannerDismissed } from './sunset-banner/sunset-banner.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {

  navigationSubscription; //used to force the component to reload; This happens when you switch accounts and are already on this view

  isLoggedIn: boolean = false;
  isLimitWarning: boolean = false;
  isSunsetBanner: boolean = false;
  isAdminBanner: boolean = false;
  isAdmin: boolean = false;
  session: any = {};
  limitWarningPercent: number = 0;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private titleService: Title,
              private authService: AuthService,
              private sessionService: SessionService,
              private eventService: EventService,
              private versionService: VersionService,
              private driftService: DriftService,
              private sentryService: SentryService,
              private serviceWorkerService: ServiceWorkerService,
              private constants: Constants) {

    // Leverage route information for dynamic titles and Drift events, based on the article here -
    //          https://toddmotto.com/dynamic-page-titles-angular-2-router-events
    this.router.events
      .filter(event => event instanceof NavigationEnd)
      .map(() => this.activatedRoute)
      .map(route => {
        while (route.firstChild) route = route.firstChild;
        return route;
      })
      .filter(route => route.outlet === 'primary')
      .mergeMap(route => route.data)
      .subscribe((event: any) => {
        this.titleService.setTitle(`Drift Email | ${event['title']}`);
      });

    this.eventService.loggedIn$.subscribe((newIsLoggedIn) => {
      this.isLoggedIn = newIsLoggedIn;
    });

    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      // If it is a NavigationEnd event re-initalise the component
      if (e instanceof NavigationEnd) {
        this.sessionRefresh();
      }
    });
  }

  ngOnInit() {
    this.isLoggedIn = this.authService.isLoggedIn;
    if (this.isLoggedIn) {
      this.driftService.identify();
      this.sentryService.identify();
      this.sessionRefresh();
    }
    this.serviceWorkerService.install();
    this.versionService.boot();
  }

  ngOnDestroy() {
    // avoid memory leaks here by cleaning up after ourselves. If we
    // don't then we will continue to run our initialiseInvites()
    // method on every navigationEnd event.
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  onSunsetBannerClosed() {
    this.isSunsetBanner = false;
  }

  private sessionRefresh() {
    // sunset announcement: logged-in pages only, hidden for a day after close
    this.isSunsetBanner = this.isLoggedIn && !isSunsetBannerDismissed();

    if (this.isLoggedIn) {
      this.sessionService.getSession().subscribe(async (data: any) => {
        this.session = data;
        // limit warning
        const utilizationPercentage = get(this.session, 'account_utilization_percentage', null);
        this.limitWarningPercent = this.constants.limitWarningPercent;
        this.isLimitWarning = utilizationPercentage > this.limitWarningPercent;
        // admin account banner
        this.isAdmin = await this.authService.hasPermission('sadmin');
        const account = get(this.session, 'account', null);
        this.isAdminBanner = this.isAdmin && account !== 'Siftrock Demo' && account !== 'Siftrock Dev' && account !== 'Siftrock QA';
      });
    } else {
      this.isLimitWarning = false;
      this.isAdmin = false;
      this.isAdminBanner = false;
    }
  }
}



// WEBPACK FOOTER //
// ./client/app/app.component.ts